import { useState, useRef, useEffect } from "react";
import {
  ArrowLeft, ShieldCheck, ShieldPlus, Lock, X, Landmark, IdCard,
  LineChart, KeyRound, Tv, Receipt, Terminal,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import BlueprintCanvas from "@/components/dashboard/BlueprintCanvas";
import GamificationDesk from "@/components/dashboard/GamificationDesk";
import { UpgradeLockedRow, GovDocVault, GovStepUp, useFounder } from "@/components/dashboard/VaultStage7b";
import AssetCanvasPanel from "@/components/dashboard/vault/AssetCanvasPanel";
import CredentialsPanel from "@/components/dashboard/vault/CredentialsPanel";
import ContentRemotePanel from "@/components/dashboard/vault/ContentRemotePanel";
import TaxStudioPanel from "@/components/dashboard/vault/TaxStudioPanel";
import AdminPanel from "@/components/dashboard/vault/AdminPanel";
import WalletLocker from "@/components/dashboard/vault/WalletLocker";

const TIER_RANK = { tier_1: 1, tier_2: 2, tier_3: 3 };
// Package-Badge gating: minimum tier rank required to open each row.
const ROW_MIN_TIER = { gate: 1, byok: 1, brand: 2, tax: 2, canvas: 3, assets: 2 };

// Stage 10 — streamlined 5-module Secure Vault dashboard (vertical stack).
const MODULES = [
  { key: "asset", title: "Asset Canvas", sub: "Track Net Worth, Wallets & Equity Tickers", Icon: LineChart, color: "#D4AF37" },
  { key: "credentials", title: "System Credentials", sub: "Manage Security PINs & BYOK Registry Keys", Icon: KeyRound, color: "#3A3A3A" },
  { key: "content", title: "Content Orchestration", sub: "Manage Walkthroughs, Logos & Landing Reels", Icon: Tv, color: "#FFD028" },
  { key: "tax", title: "Tax Accounting Studio", sub: "Automate Inbound Receipt Webhooks & Scraping", Icon: Receipt, color: "#C9A227" },
  { key: "admin", title: "Admin System Panel", sub: "System Diagnostics, User Access Control & Global API Metrics", Icon: Terminal, color: "#FF4D4D" },
];
const MOD_TITLE = Object.fromEntries(MODULES.map((m) => [m.key, m.title]));

function VaultModuleCard({ mod, onOpen }) {
  const { key, title, sub, Icon, color } = mod;
  return (
    <button onClick={() => onOpen(key)} data-testid={`vault-card-${key}`}
      className="w-full text-left flex items-center gap-3 border-4 rounded-[24px] bg-charcoal p-4 shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
      style={{ borderColor: color }}>
      <span className="w-12 h-12 rounded-2xl grid place-items-center border-2 shrink-0" style={{ borderColor: color }}>
        <Icon size={22} style={{ color }} />
      </span>
      <div className="min-w-0">
        <p className="font-serif text-lg text-white leading-tight">{title}</p>
        <p className="font-mono text-[8px] uppercase tracking-widest text-white/45 mt-1 leading-snug">{sub}</p>
      </div>
    </button>
  );
}

/* ============================= REPOSITORY MATRIX ======================= */
export default function VaultRepository({ onBack, onOpenSecurity, onReconfig, onUpgrade, initialMod = null }) {
  const { user } = useSession();
  const isFounder = useFounder();
  const tierPackage = user?.tierPackage || user?.subscription_tier || "tier_1";
  const rank = isFounder ? 99 : (TIER_RANK[tierPackage] || 1);
  const gate = (key, node) => (rank >= ROW_MIN_TIER[key] ? node : <UpgradeLockedRow rowKey={key} onUpgrade={onUpgrade} title={{
    gate: "Gate Credentials", byok: "BYOK Registry", brand: "Brand Assets",
    tax: "Tax Accounting", canvas: "Portfolio Growth", assets: "Asset Canvas",
  }[key]} min={ROW_MIN_TIER[key]} />);

  const [trap, setTrap] = useState(false);
  const [govStepUp, setGovStepUp] = useState(false);
  const [govOpen, setGovOpen] = useState(false);
  const [blueprintOpen, setBlueprintOpen] = useState(false);
  const [gamifyOpen, setGamifyOpen] = useState(false);
  const [walletLockerOpen, setWalletLockerOpen] = useState(false);
  const [mod, setMod] = useState(initialMod); // active module (null = 5-card dashboard)
  const pressTimer = useRef(null);

  // Founder proof-case shortcut: unlocking the Vault routes the founder straight
  // into the Tax Accounting Studio (Compliance Desk) once, per Master Directive
  // Stage 2. They can still navigate back to the module grid freely afterwards.
  const autoRouted = useRef(initialMod != null);
  useEffect(() => {
    if (isFounder && !autoRouted.current) { autoRouted.current = true; setMod("tax"); }
  }, [isFounder]);

  // 3s long-press on the secret Tax boundary opens the hidden gov vault.
  const startPress = () => { if (isFounder) pressTimer.current = setTimeout(() => setGovStepUp(true), 3000); };
  const cancelPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-repository">
      {trap && (
        <div className="absolute inset-0 z-50 bg-black/85 flex items-end" data-testid="founder-trapdoor">
          <div className="w-full bg-charcoal border-t-4 border-gold rounded-t-3xl p-5 space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-gold/20">
              <Lock size={16} className="text-gold" />
              <h3 className="font-serif text-xl text-white flex-1">Tier 2 · Founder Repository</h3>
              <button onClick={() => setTrap(false)} data-testid="trapdoor-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-gold bg-white/10"><X size={15} className="text-gold" /></button>
            </div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-rust">Hard liability block · read-only</p>
            <div className="flex items-start gap-2 border-2 border-gold/40 rounded-xl bg-white/5 p-3" data-testid="trapdoor-block-govid">
              <IdCard size={16} className="text-gold mt-0.5 shrink-0" />
              <p className="font-mono text-[10px] text-white/70 leading-relaxed">🪪 Government ID Information (Placeholder Only - Locked pending legal documentation clearance)</p>
            </div>
            <div className="flex items-start gap-2 border-2 border-gold/40 rounded-xl bg-white/5 p-3" data-testid="trapdoor-block-banking">
              <Landmark size={16} className="text-gold mt-0.5 shrink-0" />
              <p className="font-mono text-[10px] text-white/70 leading-relaxed">🏦 Banking & Routing Registry (Placeholder Only - Locked pending legal documentation clearance)</p>
            </div>
          </div>
        </div>
      )}

      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
        <button onClick={() => (mod ? setMod(null) : onBack())} data-testid="repo-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <span className="relative w-10 h-10 rounded-xl bg-white/10 border-2 border-gold grid place-items-center">
          <ShieldCheck size={20} className="text-gold" />
          {isFounder && (
            <button onClick={() => setTrap(true)} data-testid="founder-secret-badge" aria-label="Founder repository"
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rust border-2 border-charcoal active:scale-90" />
          )}
        </span>
        <div className="leading-none flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Unlocked · {isFounder ? "Founder" : "Member"}</p>
          <h1 className="font-serif text-2xl text-white">Secure Vault</h1>
        </div>
        <button onClick={onOpenSecurity} data-testid="vault-security-btn" aria-label="Security Suite" className="w-10 h-10 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ShieldPlus size={18} className="text-charcoal" /></button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4" data-testid="vault-body">
        {!mod ? (
          <div className="flex flex-col gap-4" data-testid="vault-dashboard-grid">
            {MODULES.map((m) => <VaultModuleCard key={m.key} mod={m} onOpen={setMod} />)}
          </div>
        ) : (
          <div className="space-y-3" data-testid={`vault-module-${mod}`}>
            <div className="flex items-center justify-between">
              <button onClick={() => setMod(null)} data-testid="vault-mod-back"
                className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-widest text-gold">
                <ArrowLeft size={14} /> All modules
              </button>
              <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{MOD_TITLE[mod]}</span>
            </div>

            {mod === "asset" && <AssetCanvasPanel gate={gate} onOpenRewards={() => setGamifyOpen(true)} onOpenWalletLocker={() => setWalletLockerOpen(true)} />}
            {mod === "credentials" && <CredentialsPanel gate={gate} user={user} onReconfig={onReconfig} />}
            {mod === "content" && <ContentRemotePanel gate={gate} onOpenBlueprint={() => setBlueprintOpen(true)} />}
            {mod === "tax" && <TaxStudioPanel gate={gate} onGovPressStart={startPress} onGovPressCancel={cancelPress} />}
            {mod === "admin" && <AdminPanel isFounder={isFounder} />}
          </div>
        )}
      </div>

      {govStepUp && <GovStepUp onPass={() => { setGovStepUp(false); setGovOpen(true); }} onClose={() => setGovStepUp(false)} />}
      {govOpen && <GovDocVault onClose={() => setGovOpen(false)} />}
      {blueprintOpen && <BlueprintCanvas onClose={() => setBlueprintOpen(false)} />}
      {gamifyOpen && <GamificationDesk onBack={() => setGamifyOpen(false)} />}
      {walletLockerOpen && <WalletLocker onBack={() => setWalletLockerOpen(false)} />}
    </div>
  );
}
